/**\n * Centralized Upload Store\n * \n * Manages all state and logic for CSV file uploads with:\n * - File upload with chunking support\n * - Server-side CSV processing and metadata extraction\n * - Entry count calculation\n * - Confirmation settings management\n */

import create from "zustand";
import { 
  EntriesUpload, 
  FileStructure, 
  CsvConfirmationSettings, 
  UploadStatus 
} from "../../types/DrawData";
import { apiService } from "../../services/api";
import { 
  ERROR_MESSAGES,
  validateDrawId,
  setErrorState,
  extractStoredFilenameFromUppyResponse 
} from "../../utils/errorUtils";
import { validateFile } from "../../utils/validationUtils";

// Re-export for backward compatibility
export { UploadStatus };
// Backward compatibility constant
export const UPLOAD_STATUS = UploadStatus;

// Helper function to calculate entry count
const calculateEntryCount = (rowCount: number, hasHeader: boolean): number => {
  return hasHeader ? Math.max(0, rowCount - 1) : rowCount;
};

// ==================== Store Interfaces ====================

interface UploadState {
  status: UploadStatus;
  error?: string;
  entriesUpload: EntriesUpload;
  csvConfirmationSettings: CsvConfirmationSettings;
  // Calculated entry count (rowCount - header if present)
  calculatedEntryCount: number;
  // Current draw ID for API calls
  currentDrawId?: string;
  // Selected file reference
  selectedFile?: File;
}

interface UploadActions {
  // File upload
  uploadFile: (file: File, drawId?: string) => Promise<void>;
  
  // File deletion
  deleteFile: () => void;
  
  // Confirmation settings (Step 2)
  setHeaderRow: (isHeaderRow: boolean) => void;
  setFileStructure: (structure: FileStructure) => void;
  setUniqueIdColumn: (column: string) => void;
  setUniqueIdentifierColumns: (columns: string[]) => void;
  setTicketCountColumn: (column: string) => void;
  setParticipantIdentificationColumn: (column: string | undefined) => void;
  
  // Utility functions
  commitToDraw: () => {
    uploadedEntries: EntriesUpload;
    csvConfirmationSettings: CsvConfirmationSettings;
    calculatedEntryCount: number;
  };
  reset: () => void;
  fullReset: () => void;
  softReset: () => void;
  getCalculatedEntryCount: () => number;
  setCurrentDrawId: (drawId: string) => void;
  
  // Uppy upload handling
  handleUppyUploadComplete: (uploadResponse: any, drawId?: string) => Promise<void>;

  hydrateFromDraw: (
    drawId: string,
    entriesUpload: EntriesUpload,
    csvConfirmationSettings: CsvConfirmationSettings
  ) => void;
}

type UploadStore = UploadState & UploadActions;

// ==================== Utility Exports ====================

export const getRowCount = (entriesUpload: EntriesUpload): number => {
  return entriesUpload.rowCount ?? 0;
};

export const getHeaderRowColumns = (entriesUpload: EntriesUpload): string[] => {
  return entriesUpload.headerRowColumns ?? [];
};

export const getEntryRow1Columns = (entriesUpload: EntriesUpload): string[] => {
  return entriesUpload.entryRow1Columns ?? [];
};

export const getEntryRow2Columns = (entriesUpload: EntriesUpload): string[] => {
  return entriesUpload.entryRow2Columns ?? [];
};

// ==================== Initial State ====================

const initialState: UploadState = {
  status: UploadStatus.IDLE,
  error: undefined,
  entriesUpload: {},
  csvConfirmationSettings: {},
  calculatedEntryCount: 0,
  currentDrawId: undefined,
  selectedFile: undefined,
};

// ==================== Store Implementation ====================

const useUploadStore = create<UploadStore>((set: any, get: any) => ({
  ...initialState,

  setCurrentDrawId: (drawId: string) => {
    const { currentDrawId: previousDrawId } = get();
    
    // If drawId is changing to a new draw (empty or different ID), perform full reset
    // This ensures no stale file data or CSV settings from previous draws
    if (drawId !== previousDrawId && (!drawId || drawId !== previousDrawId)) {
      set({ ...initialState, currentDrawId: drawId });
    } else {
      set({ currentDrawId: drawId });
    }
  },

  uploadFile: async (file: File, drawId?: string) => {
    const { currentDrawId } = get();
    const targetDrawId = validateDrawId(set, drawId, currentDrawId, "file upload");
    
    if (!targetDrawId) return;

    // Validate file before upload
    const fileValidation = validateFile(file);
    if (!fileValidation.valid) {
      set({
        status: UploadStatus.ERROR,
        error: fileValidation.error,
        selectedFile: file,
        entriesUpload: {
          originalFileName: file.name,
          size: file.size,
          mimeType: file.type,
        },
      });
      return;
    }

    set({ 
      status: UploadStatus.UPLOADING, 
      error: undefined,
      selectedFile: file,
      entriesUpload: {
        originalFileName: file.name,
        size: file.size,
        mimeType: file.type,
      },
    });

    try {
      const uploadResponse = await apiService.uploadEntryFile(targetDrawId, file);
      
      // Check if the backend returned an error message (parsing failed)
      if (uploadResponse.status === 'failed' && uploadResponse.errorMessage) {
        // Upload succeeded but CSV parsing failed - show error
        set((state: any) => ({
          status: UploadStatus.ERROR,
          error: uploadResponse.errorMessage,
          entriesUpload: {
            ...state.entriesUpload,
            originalFileName: uploadResponse.originalFileName,
            storedFileName: uploadResponse.storedFileName,
            size: uploadResponse.size,
            mimeType: uploadResponse.mimeType,
            uploadedAt: uploadResponse.uploadedAt || new Date().toISOString(),
            status: uploadResponse.status,
          },
        }));
        return;
      }
      
      // Update state with successful upload response including metadata
      set((state: any) => ({
        status: UploadStatus.SUCCESS,
        entriesUpload: {
          ...state.entriesUpload,
          originalFileName: uploadResponse.originalFileName,
          storedFileName: uploadResponse.storedFileName,
          size: uploadResponse.size,
          mimeType: uploadResponse.mimeType,
          uploadedAt: uploadResponse.uploadedAt || new Date().toISOString(),
          status: uploadResponse.status,
          // Include CSV preview data directly from upload response
          ...(uploadResponse.csvPreviewData && {
            rowCount: uploadResponse.csvPreviewData.rowCount,
            headerRowColumns: uploadResponse.csvPreviewData.headerRowColumns,
            entryRow1Columns: uploadResponse.csvPreviewData.entryRow1Columns,
            entryRow2Columns: uploadResponse.csvPreviewData.entryRow2Columns,
          }),
        },
      }));
    } catch (error) {
      setErrorState(set, error, ERROR_MESSAGES.UPLOAD_FAILED, true);
    }
  },

  deleteFile: () => {
    const { currentDrawId, entriesUpload } = get();
    
    set({ status: UploadStatus.DELETING });
    
    if (currentDrawId && entriesUpload.storedFileName) {
      apiService.deleteEntryFile(currentDrawId, entriesUpload.storedFileName)
        .then(() => {
          set(initialState);
        })
        .catch((error) => {
          set({
            ...initialState,
            error: `Warning: Failed to delete file from server: ${error.message}`,
          });
        });
    } else {
      setTimeout(() => {
        set(initialState);
      }, 100);
    }
  },

  setHeaderRow: (isHeaderRow: boolean) => {
    set((state: any) => {
      const rowCount = getRowCount(state.entriesUpload);
      
      return {
        csvConfirmationSettings: { 
          ...state.csvConfirmationSettings, 
          isHeaderRow,
        },
        calculatedEntryCount: calculateEntryCount(rowCount, isHeaderRow),
      };
    });
  },

  setFileStructure: (fileStructure: FileStructure) => {
    set((state: any) => ({
      csvConfirmationSettings: {
        ...state.csvConfirmationSettings,
        fileStructure,
        uniqueIdColumn: undefined,
        uniqueIdentifierColumns: undefined,
        ticketCountColumn: undefined,
      },
    }));
  },

  setParticipantIdentificationColumn: (participantIdentificationColumn: string | undefined) => {
    set((state: any) => ({
      csvConfirmationSettings: { 
        ...state.csvConfirmationSettings, 
        participantIdentificationColumn,
      },
    }));
  },

  setUniqueIdColumn: (uniqueIdColumn: string) => {
    set((state: any) => ({
      csvConfirmationSettings: { 
        ...state.csvConfirmationSettings, 
        uniqueIdColumn,
      },
    }));
  },

  setUniqueIdentifierColumns: (uniqueIdentifierColumns: string[]) => {
    set((state: any) => ({
      csvConfirmationSettings: {
        ...state.csvConfirmationSettings,
        uniqueIdentifierColumns,
      },
    }));
  },

  setTicketCountColumn: (ticketCountColumn: string) => {
    set((state: any) => ({
      csvConfirmationSettings: { 
        ...state.csvConfirmationSettings, 
        ticketCountColumn,
      },
    }));
  },

  commitToDraw: () => {
    const { entriesUpload, csvConfirmationSettings, calculatedEntryCount } = get();
    
    // Validation: Ensure we have required data before committing
    if (!entriesUpload.originalFileName) {
      // Return empty state if no file is uploaded
      return {
        uploadedEntries: {},
        csvConfirmationSettings: {},
        calculatedEntryCount: 0,
      };
    }
    
    return {
      uploadedEntries: {
        ...entriesUpload,
        calculatedEntryCount,
      },
      csvConfirmationSettings,
      calculatedEntryCount,
    };
  },

  reset: () => {
    const { currentDrawId } = get();
    set({ ...initialState, currentDrawId });
  },

  fullReset: () => {
    set(initialState);
  },

  softReset: () => {
    set((state: any) => ({
      csvConfirmationSettings: {},
      calculatedEntryCount: 0, // Reset calculated count when resetting settings
    }));
  },

  getCalculatedEntryCount: () => {
    const { calculatedEntryCount } = get();
    return calculatedEntryCount;
  },

  hydrateFromDraw: (
    drawId: string,
    entriesUpload: EntriesUpload,
    csvConfirmationSettings: CsvConfirmationSettings
  ) => {
    const rowCount = entriesUpload.rowCount ?? 0;
    const isHeader = csvConfirmationSettings.isHeaderRow ?? false;
    set({
      status: entriesUpload.storedFileName
        ? UploadStatus.SUCCESS
        : UploadStatus.IDLE,
      error: undefined,
      currentDrawId: drawId,
      entriesUpload,
      csvConfirmationSettings,
      calculatedEntryCount: calculateEntryCount(rowCount, isHeader),
      selectedFile: undefined,
    });
  },

  handleUppyUploadComplete: async (uploadResponse: any, drawId?: string) => {
    const { currentDrawId } = get();
    const targetDrawId = validateDrawId(set, drawId, currentDrawId, "processing upload");
    
    if (!targetDrawId) return;

    try {
      const storedFileName = extractStoredFilenameFromUppyResponse(uploadResponse, set);
      if (!storedFileName) return;

      // Fetch complete metadata for the uploaded file
      const metadata = await apiService.getEntryMetadata(targetDrawId, storedFileName);

      set((state: any) => ({
        status: UploadStatus.SUCCESS,
        entriesUpload: {
          ...state.entriesUpload,
          originalFileName: metadata.originalFileName,
          storedFileName: metadata.storedFileName,
          size: metadata.size,
          mimeType: metadata.mimeType,
          uploadedAt: metadata.uploadedAt,
          status: metadata.status,
          // Include CSV preview data directly
          ...(metadata.csvPreviewData && {
            rowCount: metadata.csvPreviewData.rowCount,
            headerRowColumns: metadata.csvPreviewData.headerRowColumns,
            entryRow1Columns: metadata.csvPreviewData.entryRow1Columns,
            entryRow2Columns: metadata.csvPreviewData.entryRow2Columns,
          }),
        },
      }));
    } catch (error) {
      setErrorState(set, error, "Failed to process upload");
    }
  },
}));

// ==================== Typed Selectors ====================
// These selectors provide type-safe access to store state and prevent direct getState() usage

export const useUploadStatus = (): UploadStatus =>
  useUploadStore((state) => state.status);

export const useUploadError = (): string | undefined => 
  useUploadStore((state) => state.error);

export const useUploadMeta = (): EntriesUpload =>
  useUploadStore((state) => state.entriesUpload);

// CSV Confirmation Settings Selectors - proper store boundaries
export const useCsvConfirmationSettings = (): CsvConfirmationSettings =>
  useUploadStore((state) => state.csvConfirmationSettings);

export const useIsHeaderRow = (): boolean | undefined =>
  useUploadStore((state) => state.csvConfirmationSettings.isHeaderRow);

export const useFileStructure = (): FileStructure | undefined =>
  useUploadStore((state) => state.csvConfirmationSettings.fileStructure);

export const useUniqueIdColumn = (): string | undefined =>
  useUploadStore((state) => state.csvConfirmationSettings.uniqueIdColumn);

export const useTicketCountColumn = (): string | undefined =>
  useUploadStore((state) => state.csvConfirmationSettings.ticketCountColumn);

export const useParticipantIdentificationColumn = (): string | undefined =>
  useUploadStore((state) => state.csvConfirmationSettings.participantIdentificationColumn);

export const useIsUploaded = (): boolean =>
  useUploadStore((state) => 
    state.status === UploadStatus.SUCCESS
  );

export const useIsUploading = (): boolean =>
  useUploadStore((state) => 
    state.status === UploadStatus.UPLOADING
  );

export const useHasError = (): boolean =>
  useUploadStore((state) => state.status === UploadStatus.ERROR);

export const useCalculatedEntryCount = (): number =>
  useUploadStore((state) => state.calculatedEntryCount);

export const useCurrentDrawId = (): string | undefined =>
  useUploadStore((state) => state.currentDrawId);

export const useSelectedFile = (): File | undefined =>
  useUploadStore((state) => state.selectedFile);

export const useHasFile = (): boolean =>
  useUploadStore((state) => !!state.selectedFile || !!state.entriesUpload.originalFileName);

export const useHasUploadedFile = (): boolean =>
  useUploadStore((state) => !!(state.entriesUpload?.storedFileName && state.entriesUpload?.originalFileName));

export const useHasCsvConfirmationSetting = (): boolean =>
  useUploadStore((state) => {
    const rowCount = state.entriesUpload?.rowCount ?? 0;
    const headerColumns = state.entriesUpload?.headerRowColumns ?? [];
    return rowCount > 0 && headerColumns.length > 0;
  });

// Backward compatibility selector for legacy components
export const useLegacyUploadedEntries = (): EntriesUpload & { calculatedEntryCount?: number } =>
  useUploadStore((state) => ({
    ...state.entriesUpload,
    calculatedEntryCount: state.calculatedEntryCount,
  }));

// Validation helper to check if upload store is ready for commit
export const useIsReadyToCommit = (): boolean =>
  useUploadStore((state) => {
    const hasFile = !!state.entriesUpload.originalFileName;
    const isUploaded = state.status === UploadStatus.SUCCESS;
    return hasFile && isUploaded;
  });

export default useUploadStore;
